<template>
    <div class="vh-100 vw-100 d-flex flex-column overflow-hidden">
        <DefaultHeader :noMargin="true" />
        <select @change="switchBaseLayer"
            class="position-absolute bottom-0 end-0 m-3 bg-white rounded shadow form-select w-auto"
            style="z-index: 100;">
            <option value="carto-layer">Classique</option>
            <option value="esri-imagery">Satelitte</option>
        </select>
        <div ref="mapContainer" class="vh-100 vw-100 d-flex flex-column"></div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import DefaultHeader from '@/components/DefaultHeader.vue'
import BaliseService from '@/services/balise.service.js'
import FormatHelper from '@/helpers/format.helper.js'

const mapContainer = ref(null)
const map = ref(null)
const balises = ref([])
const markers = new Map()


function switchBaseLayer(event) {
    const selectedLayerId = event.target.value

    // Masquer toutes les couches de fond
    map.value.setLayoutProperty('carto-layer', 'visibility', selectedLayerId === 'carto-layer' ? 'visible' : 'none')
    map.value.setLayoutProperty('esri-imagery', 'visibility', selectedLayerId === 'esri-imagery' ? 'visible' : 'none')
}


function refreshMarkers() {
    setInterval(fetchBalises, 3000) // Rafraîchit tous les 3 secondes
}

async function fetchBalises() {
    BaliseService.getGroupBalisesLastPositions()
        .then(response => {
            balises.value = FormatHelper.balisesResponseFormat(response.data);
            updateMarkers();
        })
        .catch(e => {
            console.log(e);
        });
}


function updateMarkers() {
    // Ajoute ou met à jour les marqueurs
    balises.value.forEach(balise => {
        const lngLat = [parseFloat(balise.lastLongitude), parseFloat(balise.lastLatitude)]
        const updatedAt = new Date(balise.lastSeen).getTime() // Récupérer le timestamp de la dernière mise à jour

        const markerObj = markers.get(balise.id)
        const iconUrl = `/img/cars/${balise.icon}`

        if (markerObj) {
            // Animation fluide
            animateMarkerWithRealTime(markerObj, lngLat, updatedAt)

            // Met à jour l'icône si nécessaire
            const el = markerObj.marker.getElement()
            if (el.style.backgroundImage !== `url("${iconUrl}")`) {
                el.style.backgroundImage = `url(${iconUrl})`
            }
        } else {
            // Création du marqueur
            const el = document.createElement('div')
            el.className = 'custom-marker'
            el.style.backgroundImage = `url(${iconUrl})`

            const marker = new maplibregl.Marker({ element: el })
                .setLngLat(lngLat)
                .setPopup(new maplibregl.Popup().setText(balise.name || ''))
                .addTo(map.value)

            markers.set(balise.id, {
                marker,
                lastUpdatedAt: updatedAt,
                currentPosition: lngLat // Ajout de la position actuelle pour la comparaison
            })
        }
    })
}


function animateMarkerWithRealTime(markerObj, to, newTimestamp) {
    const { marker, lastUpdatedAt, currentPosition } = markerObj
    const from = currentPosition

    if (JSON.stringify(lastUpdatedAt) === JSON.stringify(newTimestamp))
        return

    // Calcul de la durée réelle en millisecondes
    const duration = Math.max(newTimestamp - lastUpdatedAt, 300) // Assure que la durée est au moins 300ms

    const start = performance.now()

    function animate(time) {
        const elapsed = time - start
        const t = Math.min(elapsed / duration, 1) // Normalisation du temps de l'animation (t entre 0 et 1)

        const lng = from[0] + (to[0] - from[0]) * t
        const lat = from[1] + (to[1] - from[1]) * t

        marker.setLngLat([lng, lat])

        if (t < 1) {
            requestAnimationFrame(animate)
        }
    }

    requestAnimationFrame(animate)

    // Mise à jour de la position et du timestamp
    markerObj.lastUpdatedAt = newTimestamp
    markerObj.currentPosition = to // Met à jour la position actuelle
}


onMounted(() => {
    map.value = new maplibregl.Map({
        container: mapContainer.value,
        style: {
            version: 8,
            sources: {
                cartoVoyager: {
                    type: 'raster',
                    tiles: [
                        'https://a.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png',
                        'https://b.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png',
                        'https://c.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png',
                        'https://d.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png'
                    ],
                    tileSize: 256,
                },
                esriImagery: {
                    type: 'raster',
                    tiles: [
                        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                    ],
                    tileSize: 256
                }
            },
            layers: [
                {
                    id: 'carto-layer',
                    type: 'raster',
                    source: 'cartoVoyager',
                    minzoom: 2,
                    maxzoom: 19
                },
                {
                    id: 'esri-imagery',
                    type: 'raster',
                    source: 'esriImagery',
                    minzoom: 0,
                    maxzoom: 19,
                    layout: { visibility: 'none' } // masquée par défaut
                }
            ]
        },
        center: [2.3522, 48.8566],
        zoom: 5,
        minZoom: 2,
        maxZoom: 18,
        attributionControl: false
    })

    map.value.addControl(new maplibregl.NavigationControl(), 'top-right')
    map.value.addControl(new maplibregl.FullscreenControl());

    map.value.on('load', () => {
        // Charger les marqueurs initialement
        fetchBalises()

        // Rafraîchir les marqueurs périodiquement
        refreshMarkers()
    })
})
</script>

<style>
.custom-marker {
    width: 48px;
    height: 48px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}
</style>